import React, { useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { apiClient, getApiErrorMessage } from "../../../lib/apiClient";

export function FinalizeRoutesDialog({ open, onOpenChange, tempRoutes, date, outlet, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState([]); // {tempId, status: 'pending'|'saved'|'failed', error, conflictIds}

  const ready = tempRoutes.filter((r) => r.deliveryIds.size > 0);
  const unoptimizedCount = ready.filter((r) => !r.optimized).length;

  const orderedIds = (r) => (r.optimized?.steps
    ? r.optimized.steps.filter((s) => s.type === "job").map((s) => s.id)
    : [...r.deliveryIds]);

  const handleConfirm = async () => {
    setSaving(true);
    const outState = ready.map((r) => ({ tempId: r.tempId, status: "pending" }));
    setResults(outState);
    let anyFailed = false;
    const savedRouteIds = [];
    for (let i = 0; i < ready.length; i++) {
      const r = ready[i];
      if (anyFailed) { outState[i] = { tempId: r.tempId, status: "skipped" }; setResults([...outState]); continue; }
      try {
        const { data } = await apiClient.post("/routes/plan", {
          driver_id: r.driverId,
          date,
          starting_point: outlet?.latitude ? JSON.stringify([parseFloat(outlet.longitude), parseFloat(outlet.latitude)]) : null,
          end_point: r.driverEndpoint?.latitude ? JSON.stringify([parseFloat(r.driverEndpoint.longitude), parseFloat(r.driverEndpoint.latitude)]) : null,
          distance: r.optimized?.distance != null ? String(r.optimized.distance) : null,
          duration: r.optimized?.duration != null ? String(r.optimized.duration) : null,
          distance_source: r.optimized?.distanceSource || null,
          delivery_ids: orderedIds(r),
        });
        outState[i] = { tempId: r.tempId, status: "saved", routeId: data.id };
        savedRouteIds.push(data.id);
      } catch (error) {
        anyFailed = true;
        const detail = error?.response?.data?.detail;
        outState[i] = {
          tempId: r.tempId, status: "failed",
          error: getApiErrorMessage(error, "Could not save this route."),
          conflictIds: detail?.conflicting_delivery_ids || [],
        };
      }
      setResults([...outState]);
    }
    setSaving(false);
    if (!anyFailed) onSuccess(savedRouteIds);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="finalize-routes-dialog">
        <DialogHeader>
          <DialogTitle>Finalize Daily Routes</DialogTitle>
          <DialogDescription>
            This saves {ready.length} route(s) for {date}. Routes are written one at a time; if one fails, the rest stop and your planning session is preserved so you can fix and retry.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 space-y-2 overflow-y-auto">
          {ready.map((r) => {
            const res = results.find((x) => x.tempId === r.tempId);
            return (
              <div key={r.tempId} className="rounded-md border border-border p-3 text-xs" data-testid={`finalize-row-${r.tempId}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-text-main">Route {r.tempId} — {r.driverName} — {r.deliveryIds.size} stops</span>
                  {res?.status === "saved" && <span className="flex items-center gap-1 text-success"><CheckCircle2 size={13} /> Saved as #{res.routeId}</span>}
                  {res?.status === "failed" && <span className="flex items-center gap-1 text-danger"><AlertTriangle size={13} /> Failed</span>}
                  {res?.status === "pending" && <span className="text-text-muted">Saving...</span>}
                  {res?.status === "skipped" && <span className="text-text-muted">Skipped</span>}
                </div>
                {res?.status === "failed" && (
                  <p className="mt-1 text-danger" data-testid={`finalize-error-${r.tempId}`}>
                    {res.error} {res.conflictIds?.length > 0 && `(conflicting deliveries: ${res.conflictIds.join(", ")})`}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {unoptimizedCount > 0 && (
          <p className="flex items-center gap-1.5 rounded-md border border-warning/30 bg-warning-bg p-2 text-xs font-medium text-warning" data-testid="finalize-dialog-unoptimized-warning">
            <AlertTriangle size={13} strokeWidth={2} /> {unoptimizedCount} of these route{unoptimizedCount > 1 ? "s" : ""} haven't been optimized — you can still save, but stop order may not be efficient.
          </p>
        )}

        <DialogFooter>
          <Button variant="secondary" disabled={saving} onClick={() => onOpenChange(false)} data-testid="finalize-cancel-button">Close</Button>
          <Button variant="accent" disabled={saving} onClick={handleConfirm} data-testid="finalize-confirm-button">
            {saving ? "Saving..." : "Confirm & Save All Routes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
